import { useMemo } from 'react';
import type { Question } from '@aphp-ema/common/questionnaire';

export interface QuestionConditionStats {
  /** Number of display conditions on this question */
  conditionsCount: number;
  /** Number of other questions that depend on this question */
  dependentQuestionsCount: number;
  /** Question IDs that depend on this question */
  dependentQuestionIds: string[];
  /** Question IDs that this question depends on */
  dependsOnQuestionIds: string[];
}

interface UseQuestionConditionStatsParams {
  currentQuestions: Question[];
  newQuestions?: Question[];
}

export const calculateQuestionConditionStats = (
  currentQuestions: Question[],
): Map<string, QuestionConditionStats> => {
  const stats = new Map<string, QuestionConditionStats>();

  currentQuestions.forEach((question) => {
    stats.set(question.id, {
      conditionsCount: 0,
      dependentQuestionsCount: 0,
      dependentQuestionIds: [],
      dependsOnQuestionIds: [],
    });
  });

  currentQuestions.forEach((question) => {
    const questionStats = stats.get(question.id);
    if (!questionStats) return;

    const conditions = question.displayConditions?.conditions || [];
    questionStats.conditionsCount = conditions.length;

    conditions.forEach((condition) => {
      if (condition.target?.source === 'question') {
        const targetId = condition.target.questionId;

        questionStats.dependsOnQuestionIds.push(targetId);

        const targetStats = stats.get(targetId);
        if (targetStats) {
          targetStats.dependentQuestionsCount++;
          targetStats.dependentQuestionIds.push(question.id);
        }
      }
    });
  });

  return stats;
};

export const useQuestionConditionStats = ({
  currentQuestions,
}: UseQuestionConditionStatsParams): Map<string, QuestionConditionStats> => {
  return useMemo(() => {
    return calculateQuestionConditionStats(currentQuestions ?? []);
  }, [currentQuestions]);
};
